import React from "react";
import { graphql, navigate } from "gatsby";
import { OurApproachHighlightedArticlesBlockFieldsFragment } from "../../../../graphql-types";


import * as styles from "./highlightedArticles.module.scss"
import useWindowWidth from "../../../hooks/use-window-width";
import BackArrowSvg from "../../../../static/vectors/backArrowV2.svg";
import ArrowSvg from "../../../../static/vectors/arrowV2.svg";
import addCross from "../../../../static/vectors/addCross.svg";
import moment from "moment";
import { ReactSVG } from "react-svg";

type HighlightedArticlesProps = {
    data: OurApproachHighlightedArticlesBlockFieldsFragment
    locale: string
    projectsNewsInsightsPageSlug: string
}

const HighlightedArticles = ({ data, locale, projectsNewsInsightsPageSlug }: HighlightedArticlesProps) => {
    const defaultLocale = "fr"
    const isMobile = useWindowWidth() <= 825
    const isTablet = useWindowWidth() <= 1300
    const [currentIndex, setCurrentIndex] = React.useState(0)
    const [touchStart, setTouchStart] = React.useState<number | null>(null)
    const handlePrevClick = () => {
        setCurrentIndex((prev) => Math.max(0, prev - 1))
      }
    
      const handleNextClick = () => {
        setCurrentIndex((prev) => Math.min(data.articles.length - (isMobile ? 1 : isTablet ? 2 : 3), prev + 1))
      }
    
      const handleTouchStart = (e: TouchEvent) => {
        setTouchStart(e.touches[0].clientX)
      }
    
      const handleTouchEnd = (e: TouchEvent) => {
        if (touchStart === null) return
    
        const touchEnd = e.changedTouches[0].clientX
        const diff = touchStart - touchEnd
    
        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            handleNextClick()
          } else {
            handlePrevClick()
          }
        }
        setTouchStart(null)
      }

      const visibleArticles = isMobile 
      ? data.articles.slice(currentIndex, currentIndex + 1)
      : isTablet
        ? data.articles.slice(currentIndex, currentIndex + 2)
        : data.articles.slice(currentIndex, currentIndex + 3)
      
    const canScrollLeft = currentIndex > 0
    const canScrollRight = currentIndex < data.articles.length - (isMobile ? 1 : isTablet ? 2 : 3)

    const handleArticleClick = (link: string) => {
        navigate(link)
    }

    const formatDescription = (description: string) => {
        return description.length > 300 ? `${description.substring(0, 300)}...` : description
    }

    return (
    <section className={styles.container}>

        <div className={styles.titleContainer}>
            <h2 className={styles.title}>{data.title}</h2>
            <p className={styles.introText}>{data.introText}</p>
        </div>
        <div 
        className={styles.articlesContainer}
        onTouchStart={(e: React.TouchEvent<HTMLDivElement>) => handleTouchStart(e.nativeEvent)}
        onTouchEnd={(e: React.TouchEvent<HTMLDivElement>) => handleTouchEnd(e.nativeEvent)}
      >
        <div className={styles.carouselButtons}>
          <button 
            className={`${styles.prevButton}`}
            aria-label="Previous slide"
            onClick={handlePrevClick}
            disabled={!canScrollLeft}
          >
            <ReactSVG className={styles.icon} src={BackArrowSvg} />
          </button>
          <button 
            className={`${styles.nextButton} `}
            aria-label="Next slide"
            onClick={handleNextClick}
            disabled={!canScrollRight}
          >
            <ReactSVG className={styles.icon} src={ArrowSvg} />
          </button>
        </div>

        {visibleArticles.map((article, index) => (
          <div key={article.slug || index} className={styles.articleCardContainer}>
            <div className={styles.articleTitleContainer}>
                <h3 className={styles.articleTitle}>{article.articleTitle}</h3>
                <span className={styles.articleInfo}>{article.category.label} {article.date ? `${moment(article.date).format("DD.MM.YYYY")}` : null}</span>
            </div>
            <p className={styles.articleDescription}>{ formatDescription(article.description)}</p>
            <button className={styles.articleButton} onClick={() => handleArticleClick(locale === defaultLocale
                ? `/${projectsNewsInsightsPageSlug}/${article.slug}`
                : `/${locale}/${projectsNewsInsightsPageSlug}/${article.slug}`)}>
                <ReactSVG className={styles.icon} src={addCross}/>
            </button>
          </div>
        ))}
      </div>
    </section>
  )
}

export const fragment = graphql`
  fragment OurApproachHighlightedArticlesBlockFields on DatoCmsOurApproachHighlightedArticlesBlock {
    __typename
    id
    title
    introText
    articles {
      articleTitle
      date
      thumbnailTitle
      description
      category {
        label
      }
      image {
        url
        alt
        format
      }

      slug
    }
  }
`

export default HighlightedArticles